import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { databases, account, APPWRITE_DB_ID, COLLECTIONS, ID, Query, testConnection, getCurrentAccount, createEmailPasswordSession, verifySession } from '../appwrite/appwrite';
import { calculateBooking, customerTotals, DEFAULT_DEDUCTION_RATE, DEFAULT_VAT_RATE, round2 } from '../lib/finance';
import { resolveLoginIdentifier } from '../lib/auth';
import { changeAccountName, changeAccountPassword } from '../lib/account';
import {
  isDuplicateCredit,
  isDuplicateExpense,
  DUPLICATE_CREDIT_MESSAGE,
  DUPLICATE_EXPENSE_MESSAGE,
} from '../lib/duplicates';

const AppContext = createContext(undefined);

/** Verhindert, dass ein hängender Netzwerkaufruf die App blockiert. */
const withTimeout = (promise, ms = 10000) =>
  Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error('Zeitüberschreitung')), ms)),
  ]);

const safeCreateDocument = async (dbId, colId, docId, data) => {
  let currentData = { ...data };
  let hasAlerted = false;
  
  while (true) {
    try {
      return await databases.createDocument(dbId, colId, docId, currentData);
    } catch (error) {
      const match = error.message?.match(/Unknown attribute: "([^"]+)"/);
      if (match && match[1]) {
        const missingAttr = match[1];
        if (!hasAlerted) {
           console.warn(`Appwrite schema missing attribute: ${missingAttr}. Removing from payload. Bitte im Appwrite Dashboard als 'String (Array)' hinzufügen.`);
           hasAlerted = true;
        }
        delete currentData[missingAttr];
        
        if (Object.keys(currentData).length === 0) throw new Error("Alle Felder wurden entfernt. Speichern fehlgeschlagen.");
      } else {
        throw error;
      }
    }
  }
};

const safeUpdateDocument = async (dbId, colId, docId, data) => {
  let currentData = { ...data };
  
  while (true) {
    try {
      return await databases.updateDocument(dbId, colId, docId, currentData);
    } catch (error) {
      const match = error.message?.match(/Unknown attribute: "([^"]+)"/);
      if (match && match[1]) {
        const missingAttr = match[1];
        console.warn(`Appwrite schema missing attribute: ${missingAttr}. Removing from update payload.`);
        delete currentData[missingAttr];
        if (Object.keys(currentData).length === 0) return { $id: docId, ...data };
      } else {
        throw error;
      }
    }
  }
};

const mapDoc = (doc) => {
  // Ignoriere eventuell manuell angelegte 'created_at' Felder, um das echte $createdAt zu priorisieren
  const { $id, $collectionId, $databaseId, $createdAt, $updatedAt, $permissions, created_at, updated_at, ...rest } = doc;
  const parsed = { ...rest };
  if (parsed.checklist && typeof parsed.checklist === 'string') {
    try {
      parsed.checklist = JSON.parse(parsed.checklist);
    } catch (e) {
      parsed.checklist = [];      console.warn('Checklist parse error:', e.message);
    }
  }
  return { 
    id: $id, 
    created_at: $createdAt || created_at || new Date().toISOString(), 
    updated_at: $updatedAt || updated_at || new Date().toISOString(), 
    ...parsed 
  };
};

const FRANCHISE_RATE = 0;

/**
 * Entfernt Dubletten aus den Task-Status: es bleibt je status_id nur EIN Dokument
 * (das älteste). Grund: die Collection kann Duplikate enthalten (z. B. durch einen
 * Race zwischen Login und Datenladen) – dann zeigt das Kanban-Board jede Spalte
 * mehrfach.
 */
const dedupeByStatusId = (list) => {
  const seen = new Set();
  return list.filter((s) => {
    const key = s.status_id || s.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Kompatibilitäts-Wrapper. Die verbindliche Rechenlogik liegt in src/lib/finance.js.
 * Provision-Basis ist der Bruttobetrag, our_share = netto − provision.
 */
export const calcRevenue = (grossAmount, vatRate, commissionRate) =>
  calculateBooking({ gross: grossAmount, vatRate, commissionRate });

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [connectionError, setConnectionError] = useState(null);

  // ── BENUTZER ──────────────────────────────────────────────────────────
  // Die Konten werden serverseitig in Appwrite verwaltet. Es gibt KEINE
  // Passwörter und keine Credentials im Frontend-Bundle.
  // VITE_APPWRITE_TEAM_USERS: JSON-Liste der Team-Mitglieder (id, name, email)
  const [users, setUsers] = useState(() => {
    const list = [];
    try {
      const raw = import.meta.env.VITE_APPWRITE_TEAM_USERS;
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) list.push(...parsed);
    } catch (e) {
      console.warn('Team-User-Konfiguration ungültig:', e.message);
    }
    // n8n ist ein technischer Platzhalter für den Auto-Sync-Agenten — kein Login
    if (!list.find(u => u.name === 'n8n')) {
      list.push({ id: 'n8n', name: 'n8n', color: 'bg-purple-500', is_ai: true });
    }
    return list;
  });

  const humanUsers = useMemo(() => users.filter(u => !u.is_ai), [users]);

  const [expenses,   setExpenses]   = useState([]);
  const [customers,  setCustomers]  = useState([]);
  const [credits,    setCredits]    = useState([]);
  const [tasks,        setTasks]        = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  // true = die Collection "task_statuses" wurde erfolgreich aus Appwrite gelesen.
  // Bis dahin darf TaskStatusRecovery KEINE Status anlegen – sonst legt sie
  // beim Login die Defaults doppelt an (login() setzt isAuthenticated VOR dem
  // Datenladen, taskStatuses ist zu diesem Zeitpunkt noch leer).
  const [taskStatusesLoaded, setTaskStatusesLoaded] = useState(false);

  // Default-Status-Konfiguration für Auto-Migration
  const DEFAULT_STATUSES = [
    { status_id: 'todo',       label: 'Zu erledigen', color: 'bg-gray-100',   text_color: 'text-gray-700',   icon: 'FiCircle',     order: 1, is_default: true },
    { status_id: 'inprogress', label: 'In Arbeit',    color: 'bg-blue-100',   text_color: 'text-blue-700',   icon: 'FiLoader',     order: 2, is_default: true },
    { status_id: 'waiting',    label: 'Warten',       color: 'bg-yellow-100', text_color: 'text-yellow-700', icon: 'FiClock',      order: 3, is_default: true },
    { status_id: 'done',       label: 'Erledigt',     color: 'bg-green-100',  text_color: 'text-green-700',  icon: 'FiCheckCircle', order: 4, is_default: true },
  ];
  const [auditLogs,  setAuditLogs]  = useState([]);
  const [commissionHistory, setCommissionHistory] = useState([]);
  const [loading,    setLoading]    = useState(true);
  // Fehler beim Laden der Daten: werden NICHT still verschluckt, sonst zeigt
  // das Dashboard 0,00 € ohne erkennbare Ursache (es gab genau diesen Fall:
  // alter Browser-Cache -> Data-Reads 401 -> leere Arrays -> stille Nullen).
  const [loadError,  setLoadError]  = useState(null);

  const [settings, setSettings] = useState(() => {
    const defaults = { 
      theme: 'light', 
      language: 'de', 
      notifications: true, 
      autoBackup: false, 
      currency: 'EUR', 
      dateFormat: 'DD.MM.YYYY',
      taxSettings: {
        ignoredYears: [], // e.g. ['2024', '2025']
        personalSalaries: {} // e.g. { '2024': 50000 }
      }
    };
    try {
      const stored = localStorage.getItem('chargenest_settings');
      if (stored) {
        const parsed = JSON.parse(stored);
        return { 
          ...defaults, 
          ...parsed, 
          taxSettings: { ...defaults.taxSettings, ...(parsed.taxSettings || {}) } 
        };
      }
    } catch (e) {}
    return defaults;
  });

  useEffect(() => { localStorage.setItem('chargenest_settings', JSON.stringify(settings)); }, [settings]);

  const currentUser = useMemo(() => users.find(u => u.name === user) || null, [user, users]);

  const logAction = async (actionType, entityType, entityId, changes = null) => {
    try {
      const data = {
        user_name:   user || 'System',
        action_type: actionType,
        entity_type: entityType,
        entity_id:   entityId ? String(entityId) : null,
        changes:     changes ? JSON.stringify(changes) : null
      };
      const doc = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.AUDIT, ID.unique(), data);
      const mapped = mapDoc(doc);
      // Füge das Log direkt dem State hinzu (Echtzeit-Update in der UI)
      setAuditLogs(prev => [mapped, ...prev]);
    } catch (error) {}
  };

  const loadAllData = async () => {
    try {
      const [expRes, custRes, credRes, taskRes, auditRes, commRes, statusRes] = await Promise.allSettled([
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.EXPENSES,  [Query.orderDesc('date'),           Query.limit(5000)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS,  [Query.orderAsc('name'),            Query.limit(5000)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.CREDITS,    [Query.orderDesc('transfer_date'),  Query.limit(5000)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.TASKS,          [Query.orderDesc('$createdAt'),     Query.limit(5000)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.AUDIT,          [Query.orderDesc('$createdAt'),     Query.limit(100)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.COMMISSION,     [Query.orderDesc('$createdAt'),     Query.limit(1000)]),
        databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES,  [Query.orderAsc('order'),           Query.limit(100)]),
      ]);

      // Lokale Status laden (Fallback)
      const localStatuses = readLocalStatuses();
      // Solange die Collection nicht gelesen ist, darf die Recovery nichts anlegen.
      setTaskStatusesLoaded(false);

      // Auto-Migration: Wenn task_statuses leer ist UND keine lokalen vorhanden, lege die 4 Defaults an
      const hasNothing = (statusRes.status === 'fulfilled' && statusRes.value.documents.length === 0) && localStatuses.length === 0;
      if (hasNothing) {
        try {
          for (const def of DEFAULT_STATUSES) {
            await databases.createDocument(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES, ID.unique(), {
              ...def,
              created_at: new Date().toISOString(),
            });
          }
          // Nach Migration: nochmal laden
          const migrated = await databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES, [Query.orderAsc('order'), Query.limit(100)]);
          if (taskRes.status  === 'fulfilled') setTasks(taskRes.value.documents.map(mapDoc));
          setTaskStatuses(dedupeByStatusId(migrated.documents.map(mapDoc)));
          setTaskStatusesLoaded(true);
        } catch (migErr) {
          console.warn('⚠️ Auto-Migration task_statuses fehlgeschlagen:', migErr.message);
          if (taskRes.status  === 'fulfilled') setTasks(taskRes.value.documents.map(mapDoc));
          setTaskStatuses([]);
          setTaskStatusesLoaded(true);
        }
      } else if (statusRes.status === 'fulfilled') {
        if (taskRes.status  === 'fulfilled') setTasks(taskRes.value.documents.map(mapDoc));
        // Appwrite + local mergen
        // Duplikate (gleiche status_id) werden aussortiert – sonst zeigt das
        // Kanban-Board dieselbe Spalte mehrfach.
        const remote = dedupeByStatusId(statusRes.value.documents.map(mapDoc));
        const remoteIds = new Set(remote.map(s => s.status_id));
        const uniqueLocal = localStatuses.filter(s => !remoteIds.has(s.status_id));
        setTaskStatuses([...remote, ...uniqueLocal].sort((a, b) => (a.order || 0) - (b.order || 0)));
        setTaskStatusesLoaded(true);
      } else {
        if (taskRes.status  === 'fulfilled') setTasks(taskRes.value.documents.map(mapDoc));
        // Appwrite komplett down — nur local. taskStatusesLoaded bleibt false:
        // ohne erfolgreichen Lesezugriff wird serverseitig nichts geschrieben.
        setTaskStatuses(localStatuses);
      }
      if (expRes.status   === 'fulfilled') setExpenses(expRes.value.documents.map(mapDoc));
      if (custRes.status  === 'fulfilled') setCustomers(custRes.value.documents.map(mapDoc));
      if (credRes.status  === 'fulfilled') setCredits(credRes.value.documents.map(mapDoc));
      if (auditRes.status === 'fulfilled') setAuditLogs(auditRes.value.documents.map(mapDoc));
      if (commRes.status  === 'fulfilled') setCommissionHistory(commRes.value.documents.map(mapDoc));

      // Kritische Collections (Ausgaben, Gutschriften, Kunden) – schlaegt eines
      // davon fehl, ist das Dashboard leer und der Nutzer sieht sonst nur Nullen.
      const failures = /** @type {Array<[string, any]>} */ ([
        ['Ausgaben', expRes],
        ['Gutschriften', credRes],
        ['Kunden', custRes],
      ])
        .filter(([, result]) => result.status === 'rejected')
        .map(([label, result]) => `${label}: ${result.reason?.message || result.reason || 'unbekannter Fehler'}`);

      if (failures.length > 0) {
        const message = failures.join(' | ');
        console.error('❌ Daten konnten nicht geladen werden:', message);
        setLoadError(message);
      } else {
        setLoadError(null);
      }
    } catch (error) {
      console.error('❌ Kritischer Ladefehler:', error.message);
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  };

  /** Erneutes Laden der Daten (Button in der Fehleranzeige). */
  const reloadData = async () => {
    setLoadError(null);
    setLoading(true);
    try {
      await loadAllData();
    } catch (error) {
      console.error('❌ Neuladen fehlgeschlagen:', error?.message);
      setLoadError(error?.message || 'Neuladen fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
      // Die drei Prüfungen laufen PARALLEL: so wartet die App nur den
      // langsamsten Check (max. 6 s) statt der Summe.
      const [accountInfo, sessionProbe, conn] = await Promise.all([
        withTimeout(getCurrentAccount(), 6000).catch(() => null),
        withTimeout(
          databases.listDocuments(APPWRITE_DB_ID, COLLECTIONS.EXPENSES, [Query.limit(1)]),
          6000
        ).then(() => true).catch(() => false),
        withTimeout(testConnection(), 8000).catch(() => ({ ok: false, error: 'Zeitüberschreitung' })),
      ]);

      let authenticated = false;
      if (accountInfo) {
        setUser(accountInfo.name || accountInfo.email);
        setIsAuthenticated(true);
        authenticated = true;
      } else if (sessionProbe) {
        // Session vorhanden (Datenzugriff moeglich) -> eingeloggten User aus dem
        // lokalen Speicher uebernehmen
        const stored = localStorage.getItem('chargenest_user');
        if (stored) { setUser(stored); setIsAuthenticated(true); authenticated = true; }
      }

      if (conn?.ok) { setConnectionStatus('ok'); setConnectionError(null); }
      else           { setConnectionStatus('error'); setConnectionError(conn?.error || 'Verbindung fehlgeschlagen'); }

      if (authenticated) {
        try {
          await withTimeout(loadAllData(), 60000);
        } catch (error) {
          console.error('Daten konnten nicht geladen werden:', error?.message);
        }
      }
      } catch (error) {
        // Letzte Sicherung: die App darf NIE dauerhaft im Ladezustand hängen.
        console.error('Initialisierung fehlgeschlagen:', error?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => { cancelled = true; };
  }, []);

  // ── AUTH (echte Appwrite-Session) ─────────────────────────────────────
  // `identifier` darf eine E-Mail-Adresse ODER ein Benutzername (z. B. "Dervis90")
  // sein. Die Aufloesung Benutzername -> Konto-E-Mail passiert in lib/auth.js.
  const login = async (identifier, password) => {
    try {
      const { email, name: knownName } = resolveLoginIdentifier(identifier);
      if (!email) {
        console.error('Unbekannter Benutzername bzw. E-Mail-Adresse:', identifier);
        return false;
      }

      // Wichtig: `createEmailPasswordSession` (nicht `createSession`) — siehe appwrite.js
      await withTimeout(createEmailPasswordSession(email, String(password)), 15000);

      // Nachweis, dass die Session wirklich greift: ein authentifizierter Lesezugriff.
      const sessionOk = await withTimeout(verifySession(), 10000);
      if (!sessionOk) {
        console.error('Session erstellt, aber Datenabruf nicht autorisiert.');
        return false;
      }

      // getCurrentAccount() liefert null, falls der Account-Endpunkt der
      // Instanz für den Account-User eingeschränkt ist — der Login gilt dann
      // trotzdem als erfolgreich, solange die Session Daten liefern kann.
      const accountInfo = await withTimeout(getCurrentAccount(), 8000);
      const name = knownName || accountInfo?.name || accountInfo?.email || email;
      setUser(name);
      setIsAuthenticated(true);
      localStorage.setItem('chargenest_user', name);
      localStorage.setItem('chargenest_user_email', email);
      logAction('user_login', 'user', accountInfo?.$id || knownName || name);
      // Daten erst nach erfolgreicher Session laden
      try {
        await withTimeout(loadAllData(), 60000);
      } catch (error) {
        console.error('Daten konnten nicht geladen werden:', error?.message);
      }
      setLoading(false);
      return true;
    } catch (error) {
      console.error('Login fehlgeschlagen:', error?.message);
      return false;
    }
  };

  const logout = async () => {
    try { await account.deleteSession('current'); } catch (e) { /* Session evtl. schon weg */ }
    logAction('user_logout', 'user', currentUser?.id);
    setUser(null); setIsAuthenticated(false);
    setTaskStatusesLoaded(false);
    localStorage.removeItem('chargenest_user');
    localStorage.removeItem('chargenest_user_email');
  };

  /**
   * Benutzername des ANGEMELDETEN Kontos aendern.
   * Appwrite 1.8 / SDK 16: PUT /account/name mit {name}.
   */
  const updateUserName = async (oldName, newName) => {
    const result = await changeAccountName(account, newName);
    if (result.success) {
      setUser(result.name);
      localStorage.setItem('chargenest_user', result.name);
      logAction('user_name_updated', 'user', currentUser?.id, { old: oldName, new: result.name });
    } else {
      logAction('user_name_update_failed', 'user', currentUser?.id, { error: result.error });
    }
    return result;
  };

  /**
   * Passwort des ANGEMELDETEN Kontos aendern.
   * Appwrite 1.8: PATCH /account/password mit {password, oldPassword}
   * → SDK 16: updatePassword(neuesPasswort, altesPasswort)
   * Wichtig: das ALTE Passwort muss als zweites Argument uebergeben werden -
   * sonst lehnt der Server die Aenderung mit 401 ab.
   */
  const updateUserPassword = async (currentPassword, newPassword) => {
    const result = await changeAccountPassword(account, currentPassword, newPassword);
    if (result.success) {
      logAction('password_changed', 'user', currentUser?.id);
    } else {
      logAction('password_change_failed', 'user', currentUser?.id, { error: result.error });
    }
    return result;
  };

  const updateUser = (id, updates) => {
    logAction('user_updated', 'user', id, updates);
    return { success: false, error: 'Benutzer werden in der Appwrite-Konsole gepflegt.' };
  };

  const addUser = (name, password, is_ai = false) => {
    logAction('user_create_denied', 'user', null, { name });
    return { success: false, error: 'Neue Benutzer werden in der Appwrite-Konsole angelegt (E-Mail + Passwort).' };
  };

  const deleteUser = (id) => {
    const userToDelete = users.find(u => u.id === id);
    logAction('user_delete_denied', 'user', id, { name: userToDelete?.name });
    return { success: false, error: 'Benutzer werden in der Appwrite-Konsole deaktiviert.' };
  };

  const updateSettings = (newSettings) => {
    const oldSettings = { ...settings };
    setSettings(prev => ({ ...prev, ...newSettings }));
    logAction('settings_updated', 'settings', null, { old: oldSettings, new: newSettings });
  };

  // ── EXPENSES ──────────────────────────────────────────────────────────
  const addExpense = async (expense) => {
    try {
      // Idempotenz-Guard: identische Ausgabe (Titel + Betrag + Datum + Typ)
      // nicht doppelt anlegen.
      if (isDuplicateExpense(expenses, {
        title: expense.title,
        amount: expense.amount,
        date: expense.date,
        type: expense.type || 'expense',
      })) {
        await logAction('expense_duplicate_blocked', 'expense', null, {
          title: expense.title,
          amount: expense.amount,
          date: expense.date,
        });
        return { success: false, error: DUPLICATE_EXPENSE_MESSAGE };
      }

      const amountValue = parseFloat(expense.amount.toString().replace(',', '.'));
      // MwSt wird IMMER aus dem Bruttobetrag abgeleitet (Gutschriften 19 %)
      const vatRate = expense.vatRate !== undefined && expense.vatRate !== null
        ? parseFloat(expense.vatRate) : 0;
      const netValue = vatRate > 0 ? round2(amountValue / (1 + vatRate / 100)) : amountValue;
      const vatValue = round2(amountValue - netValue);
      const data = {
        title:      expense.title.trim(),
        amount:     amountValue,
        paid_by:    expense.paidBy,
        date:       expense.date,
        type:       expense.type || 'expense',
        image:      expense.image || '',
        category:   expense.category || '',
        vat_rate:   vatRate,
        vat_amount: vatValue,
        net_amount: netValue,
        tags:       JSON.stringify(expense.tags || []),
      };

      const doc    = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.EXPENSES, ID.unique(), data);
      const mapped = mapDoc(doc);
      setExpenses(prev => [mapped, ...prev]);
      await logAction('expense_created', 'expense', mapped.id, data);
      return { success: true };
    } catch (error) {
      console.error('Add Expense Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const updateExpense = async (id, expense) => {
    try {
      const oldExpense = expenses.find(e => e.id === id);
      const dbUpdates = {};

      if (expense.title !== undefined) dbUpdates.title = expense.title.trim();
      if (expense.amount !== undefined) dbUpdates.amount = parseFloat(expense.amount.toString().replace(',', '.'));
      if (expense.paidBy !== undefined) dbUpdates.paid_by = expense.paidBy;
      if (expense.date !== undefined) dbUpdates.date = expense.date;
      if (expense.type !== undefined) dbUpdates.type = expense.type;
      if (expense.image !== undefined) dbUpdates.image = expense.image;
      if (expense.category !== undefined) dbUpdates.category = expense.category;
      if (expense.tags !== undefined) dbUpdates.tags = JSON.stringify(expense.tags || []);

      // MwSt/Netto immer konsistent zum Bruttobetrag neu berechnen
      const gross = dbUpdates.amount !== undefined
        ? dbUpdates.amount
        : parseFloat((oldExpense?.amount || 0).toString().replace(',', '.'));
      const vatRate = expense.vatRate !== undefined && expense.vatRate !== null
        ? parseFloat(expense.vatRate)
        : parseFloat(oldExpense?.vat_rate ?? 0);
      const net = vatRate > 0 ? round2(gross / (1 + vatRate / 100)) : gross;
      dbUpdates.vat_rate = vatRate;
      dbUpdates.net_amount = net;
      dbUpdates.vat_amount = round2(gross - net);

      const doc = await safeUpdateDocument(APPWRITE_DB_ID, COLLECTIONS.EXPENSES, id, dbUpdates);
      const mapped = mapDoc(doc);
      setExpenses(prev => prev.map(e => e.id === id ? { ...e, ...mapped } : e));
      await logAction('expense_updated', 'expense', id, { updates: dbUpdates });
      return { success: true };
    } catch (error) {
      console.error('Update Expense Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const deleteExpense = async (id) => {
    const expense = expenses.find(e => e.id === id);
    setExpenses(prev => prev.filter(e => e.id !== id));
    try {
      await databases.deleteDocument(APPWRITE_DB_ID, COLLECTIONS.EXPENSES, id);
      await logAction('expense_deleted', 'expense', id, { deleted: expense });
      return { success: true };
    } catch (error) {
      console.error('Delete Expense Error:', error.message);
      setExpenses(prev => expense ? [expense, ...prev] : prev);
      return { success: false, error: error.message };
    }
  };

  // ── CUSTOMERS ─────────────────────────────────────────────────────────
  const addCustomer = async (customer) => {
    try {
      const data = {
        name:            customer.name.trim(),
        email:           customer.email?.trim()    || '',
        phone:           customer.phone?.trim()    || '',
        address:         customer.address?.trim()  || '',
        company:         customer.company?.trim()  || '',
        bank_name:       customer.bankName?.trim() || '',
        iban:            customer.iban?.trim()     || '',
        bic:             customer.bic?.trim()      || '',
        tax_id:          customer.taxId?.trim()    || '',
        notes:           customer.notes?.trim()    || '',
        commission_rate: parseFloat(customer.commissionRate ?? 0)
      };
      const doc    = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS, ID.unique(), data);
      const mapped = mapDoc(doc);
      setCustomers(prev => [...prev, mapped]);
      try {
        const commDoc = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.COMMISSION, ID.unique(), {
          customer_id: mapped.id, commission_rate: data.commission_rate
        });
        setCommissionHistory(prev => [mapDoc(commDoc), ...prev]);
      } catch (e) { console.warn('Commission history not saved:', e.message); }
      await logAction('customer_created', 'customer', mapped.id, data);
      return { success: true };
    } catch (error) {
      console.error('Add Customer Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const updateCustomer = async (id, updates) => {
    try {
      const oldCustomer = customers.find(c => c.id === id);
      const dbUpdates   = {};
      if (updates.name    !== undefined) dbUpdates.name    = updates.name.trim();
      if (updates.email   !== undefined) dbUpdates.email   = updates.email?.trim()   || '';
      if (updates.phone   !== undefined) dbUpdates.phone   = updates.phone?.trim()   || '';
      if (updates.address !== undefined) dbUpdates.address = updates.address?.trim() || '';
      if (updates.company !== undefined) dbUpdates.company = updates.company?.trim() || '';
      if (updates.bankName !== undefined) dbUpdates.bank_name = updates.bankName?.trim() || '';
      if (updates.iban    !== undefined) dbUpdates.iban    = updates.iban?.trim()    || '';
      if (updates.bic     !== undefined) dbUpdates.bic     = updates.bic?.trim()     || '';
      if (updates.taxId   !== undefined) dbUpdates.tax_id  = updates.taxId?.trim()   || '';
      if (updates.notes   !== undefined) dbUpdates.notes   = updates.notes?.trim()   || '';
      if (updates.commissionRate !== undefined) {
        const newRate = parseFloat(updates.commissionRate);
        const oldRate = oldCustomer?.commission_rate ?? 0;
        if (newRate !== oldRate) {
          dbUpdates.commission_rate = newRate;
          try {
            const commDoc = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.COMMISSION, ID.unique(), { customer_id: id, commission_rate: newRate });
            setCommissionHistory(prev => [mapDoc(commDoc), ...prev]);
          } catch (e) { console.warn('Commission history update failed:', e.message); }
          await logAction('commission_rate_changed', 'customer', id, { old_rate: oldRate, new_rate: newRate });
        }
      }
      const doc    = await safeUpdateDocument(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS, id, dbUpdates);
      const mapped = mapDoc(doc);
      setCustomers(prev => prev.map(c => c.id === id ? { ...c, ...mapped } : c));
      await logAction('customer_updated', 'customer', id, { old: oldCustomer, updates: dbUpdates });
      return { success: true };
    } catch (error) {
      console.error('Update Customer Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const deleteCustomer = async (id) => {
    const customer = customers.find(c => c.id === id);
    setCustomers(prev => prev.filter(c => c.id !== id));
    try {
      await databases.deleteDocument(APPWRITE_DB_ID, COLLECTIONS.CUSTOMERS, id);
      await logAction('customer_deleted', 'customer', id, { deleted: customer });
      return { success: true };
    } catch (error) {
      console.error('Delete Customer Error:', error.message);
      setCustomers(prev => customer ? [...prev, customer] : prev);
      return { success: false, error: error.message };
    }
  };

  // ── CREDITS ───────────────────────────────────────────────────────────
  const addCredit = async (credit) => {
    try {
      // Idempotenz-Guard: derselbe Import (Kunde + Tag + Betrag + Beschreibung)
      // darf nicht doppelt gespeichert werden. Ohne diesen Guard entstanden
      // 522 echte Kopien (3.187,68 € Anteil).
      if (isDuplicateCredit(credits, {
        customerId: credit.customerId,
        revenueAmount: credit.revenueAmount,
        date: credit.date,
        description: credit.description,
      })) {
        await logAction('credit_duplicate_blocked', 'credit', null, {
          customer_id: credit.customerId,
          amount: credit.revenueAmount,
          date: credit.date,
        });
        return { success: false, error: DUPLICATE_CREDIT_MESSAGE };
      }

      const calc = calculateBooking({
        gross: credit.revenueAmount || 0,
        vatRate: credit.vatRate || 0,
        commissionRate: credit.commissionRate || 0,
        settlementAmount: credit.settlementAmount ?? null,
        deductionRate: credit.deductionRate ?? DEFAULT_DEDUCTION_RATE,
        transferredAmount: credit.transferredAmount || 0,
      });
      const transferredAmount = calc.actualPayout;

      const data = {
        customer_id:          credit.customerId,
        revenue_amount:       calc.gross,
        net_amount:           calc.net,
        vat_rate:             calc.vatRate,
        vat_amount:           calc.vatAmount,
        franchise_amount:     calc.franchiseAmount,
        commission_rate_used: calc.commissionRate,
        commission_amount:    calc.commissionAmount,
        our_share:            calc.ourShare,
        // 25-%-Abzug (Rechargy behält 25 % vom Settlement-Brutto, wir erhalten 75 %)
        settlement_amount:    calc.settlementAmount,
        deduction_rate:       calc.deductionRate,
        deduction_amount:     calc.deductionAmount,
        // Auszahlungs-Abgleich: erwartet vs. tatsächlich
        expected_payout:      calc.expectedPayout,
        payout_difference:    calc.payoutDifference,
        transferred_amount:   transferredAmount,
        transaction_type:     credit.transactionType || 'revenue',
        amount:               calc.gross,
        description:          credit.description?.trim() || 'Umsatz',
        transfer_date:        credit.date || new Date().toISOString().split('T')[0],
        period_start:         credit.periodStart || credit.date || new Date().toISOString().split('T')[0],
        period_end:           credit.periodEnd   || credit.date || new Date().toISOString().split('T')[0],
        image:                credit.image || '',
      };

      const doc    = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.CREDITS, ID.unique(), data);
      const mapped = mapDoc(doc);
      setCredits(prev => [mapped, ...prev]);

      const customer = customers.find(c => c.id === credit.customerId);
      await logAction(
        credit.transactionType === 'revenue' ? 'revenue_added' : 'transfer_added',
        'credit', mapped.id,
        {
          customer_name:   customer?.name,
          amount:          calc.gross || transferredAmount,
          net_amount:      calc.net,
          vat_amount:      calc.vatAmount,
          franchise:       calc.franchiseAmount,
          commission_rate: calc.commissionRate,
          commission:      calc.commissionAmount,
          our_share:       calc.ourShare,
          settlement:      calc.settlementAmount,
          deduction_rate:  calc.deductionRate,
          deduction:       calc.deductionAmount,
          expected_payout: calc.expectedPayout,
          payout_diff:     calc.payoutDifference,
          period_start:    data.period_start,
          period_end:      data.period_end,
        }
      );
      return { success: true };
    } catch (error) {
      console.error('Add Credit Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const updateCredit = async (id, updatedCredit) => {
    try {
      const oldCredit = credits.find(c => c.id === id);
      const dbUpdate  = {};

      if (updatedCredit.revenueAmount !== undefined || updatedCredit.vatRate !== undefined) {
        const gross      = parseFloat((updatedCredit.revenueAmount ?? oldCredit?.revenue_amount ?? 0).toString().replace(',', '.'));
        const vatRate    = parseFloat(updatedCredit.vatRate  ?? oldCredit?.vat_rate  ?? 0);
        const commRate   = parseFloat(updatedCredit.commissionRate ?? oldCredit?.commission_rate_used ?? 0);
        const calc       = calculateBooking({
          gross,
          vatRate,
          commissionRate: commRate,
          settlementAmount: updatedCredit.settlementAmount ?? oldCredit?.settlement_amount ?? null,
          deductionRate: updatedCredit.deductionRate ?? oldCredit?.deduction_rate ?? DEFAULT_DEDUCTION_RATE,
          transferredAmount: oldCredit?.transferred_amount ?? 0,
        });
        dbUpdate.revenue_amount       = calc.gross;
        dbUpdate.amount               = calc.gross;
        dbUpdate.net_amount           = calc.net;
        dbUpdate.vat_rate             = calc.vatRate;
        dbUpdate.vat_amount           = calc.vatAmount;
        dbUpdate.franchise_amount     = calc.franchiseAmount;
        dbUpdate.commission_rate_used = calc.commissionRate;
        dbUpdate.commission_amount    = calc.commissionAmount;
        dbUpdate.our_share            = calc.ourShare;
        dbUpdate.settlement_amount    = calc.settlementAmount;
        dbUpdate.deduction_rate       = calc.deductionRate;
        dbUpdate.deduction_amount     = calc.deductionAmount;
        dbUpdate.expected_payout      = calc.expectedPayout;
      }

      if (updatedCredit.transferredAmount !== undefined) {
        const transferred = parseFloat(updatedCredit.transferredAmount.toString().replace(',', '.'));
        dbUpdate.transferred_amount = transferred;
        const expected = dbUpdate.expected_payout
          ?? parseFloat(oldCredit?.expected_payout ?? oldCredit?.commission_amount ?? 0);
        dbUpdate.expected_payout   = expected;
        dbUpdate.payout_difference = round2(expected - transferred);
      }
      if (updatedCredit.transactionType)  dbUpdate.transaction_type = updatedCredit.transactionType;
      if (updatedCredit.description != null) dbUpdate.description   = updatedCredit.description.trim();
      if (updatedCredit.date)             dbUpdate.transfer_date    = updatedCredit.date;
      if (updatedCredit.customerId)       dbUpdate.customer_id      = updatedCredit.customerId;
      if (updatedCredit.image !== undefined) dbUpdate.image         = updatedCredit.image;
      if (updatedCredit.periodStart != null) dbUpdate.period_start  = updatedCredit.periodStart;
      if (updatedCredit.periodEnd   != null) dbUpdate.period_end    = updatedCredit.periodEnd;

      const doc    = await safeUpdateDocument(APPWRITE_DB_ID, COLLECTIONS.CREDITS, id, dbUpdate);
      const mapped = mapDoc(doc);
      setCredits(prev => prev.map(c => c.id === id ? { ...c, ...mapped } : c));
      await logAction('credit_updated', 'credit', id, { old: oldCredit, updates: dbUpdate });
      return { success: true };
    } catch (error) {
      console.error('Update Credit Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const deleteCredit = async (id) => {
    const credit = credits.find(c => c.id === id);
    setCredits(prev => prev.filter(c => c.id !== id));
    try {
      await databases.deleteDocument(APPWRITE_DB_ID, COLLECTIONS.CREDITS, id);
      await logAction('credit_deleted', 'credit', id, { deleted: credit });
      return { success: true };
    } catch (error) {
      console.error('Delete Credit Error:', error.message);
      setCredits(prev => credit ? [credit, ...prev] : prev);
      return { success: false, error: error.message };
    }
  };

  // ── TASKS ─────────────────────────────────────────────────────────────
  const addTask = async (task) => {
    try {
      const data = {
        title:       task.title.trim(),
        description: task.description?.trim() || '',
        customer_id: task.customerId || null,
        priority:    task.priority   || 'medium',
        status:      task.status     || 'todo',
        assigned_to: task.assignedTo || null,
        due_date:    task.dueDate    || null,
        checklist:   task.checklist && task.checklist.length > 0 ? JSON.stringify(task.checklist) : '[]'
      };
      const doc    = await safeCreateDocument(APPWRITE_DB_ID, COLLECTIONS.TASKS, ID.unique(), data);
      const mapped = mapDoc(doc);
      setTasks(prev => [mapped, ...prev]);
      await logAction('task_created', 'task', mapped.id, data);
      return { success: true };
    } catch (error) {
      console.error('Add Task Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const updateTask = async (id, updates) => {
    try {
      const oldTask   = tasks.find(t => t.id === id);
      const dbUpdates = {};
      if (updates.title        !== undefined) dbUpdates.title        = updates.title;
      if (updates.description  !== undefined) dbUpdates.description  = updates.description;
      if (updates.customerId   !== undefined) dbUpdates.customer_id  = updates.customerId || null;
      if (updates.priority     !== undefined) dbUpdates.priority     = updates.priority;
      if (updates.status       !== undefined) dbUpdates.status       = updates.status;
      if (updates.assignedTo   !== undefined) dbUpdates.assigned_to  = updates.assignedTo || null;
      if (updates.dueDate      !== undefined) dbUpdates.due_date     = updates.dueDate    || null;
      if (updates.checklist    !== undefined) dbUpdates.checklist    = JSON.stringify(updates.checklist || []);
      const doc    = await safeUpdateDocument(APPWRITE_DB_ID, COLLECTIONS.TASKS, id, dbUpdates);
      const mapped = mapDoc(doc);
      setTasks(prev => prev.map(t => t.id === id ? { ...t, ...mapped } : t));
      await logAction('task_updated', 'task', id, { old: oldTask, updates: dbUpdates });
      return { success: true };
    } catch (error) {
      console.error('Update Task Error:', error.message);
      return { success: false, error: error.message };
    }
  };

  const deleteTask = async (id) => {
    const task = tasks.find(t => t.id === id);
    setTasks(prev => prev.filter(t => t.id !== id));
    try {
      await databases.deleteDocument(APPWRITE_DB_ID, COLLECTIONS.TASKS, id);
      await logAction('task_deleted', 'task', id, { deleted: task });
      return { success: true };
    } catch (error) {
      console.error('Delete Task Error:', error.message);
      setTasks(prev => task ? [task, ...prev] : prev);
      return { success: false, error: error.message };
    }
  };

  const moveTask = (taskId, newStatus) => updateTask(taskId, { status: newStatus });

  // ── TASK STATUSES ────────────────────────────────────────────────────
  // localStorage-Fallback, falls Appwrite-Collection fehlt
  const LS_STATUSES_KEY = 'chargenest_task_statuses_local';
  const readLocalStatuses = () => {
    try {
      const raw = localStorage.getItem(LS_STATUSES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  };
  const writeLocalStatuses = (arr) => {
    try { localStorage.setItem(LS_STATUSES_KEY, JSON.stringify(arr)); } catch {}
  };

  const addTaskStatus = async (status) => {
    const maxOrder = taskStatuses.length > 0
      ? Math.max(...taskStatuses.map(s => s.order || 0))
      : 0;
    const data = {
      status_id:  status.status_id  || ('status_' + Date.now()),
      label:      status.label,
      color:      status.color      || 'bg-gray-100',
      text_color: status.text_color || 'text-gray-700',
      icon:       status.icon       || 'FiCircle',
      order:      status.order      || (maxOrder + 1),
      is_default: status.is_default || false,
      created_at: new Date().toISOString(),
    };
    try {
      const doc = await databases.createDocument(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES, ID.unique(), data);
      setTaskStatuses(prev => [...prev, mapDoc(doc)].sort((a, b) => (a.order || 0) - (b.order || 0)));
      return { success: true, mode: 'appwrite' };
    } catch (error) {
      // Appwrite fehlgeschlagen — fallback auf localStorage
      console.warn('Appwrite addTaskStatus fehlgeschlagen, nutze localStorage:', error.message);
      const localDoc = { ...data, $id: 'local_' + Date.now(), id: 'local_' + Date.now() };
      const updated = [...taskStatuses, localDoc].sort((a, b) => (a.order || 0) - (b.order || 0));
      setTaskStatuses(updated);
      writeLocalStatuses(updated);
      return { success: true, mode: 'local', warning: 'Gespeichert nur lokal – der Server war nicht erreichbar oder die Anmeldung war nicht aktiv.' };
    }
  };

  const updateTaskStatus = async (id, updates) => {
    // local-only Status (lokal angelegt)?
    const isLocal = id.startsWith('local_');
    if (isLocal) {
      const updated = taskStatuses.map(s => s.id === id ? { ...s, ...updates } : s).sort((a, b) => (a.order || 0) - (b.order || 0));
      setTaskStatuses(updated);
      writeLocalStatuses(updated);
      return { success: true, mode: 'local' };
    }
    try {
      const doc = await databases.updateDocument(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES, id, updates);
      setTaskStatuses(prev => prev.map(s => s.id === id ? { ...s, ...mapDoc(doc) } : s).sort((a, b) => (a.order || 0) - (b.order || 0)));
      return { success: true };
    } catch (error) {
      // Fallback: in localStorage aktualisieren
      console.warn('Appwrite updateTaskStatus fehlgeschlagen, nutze localStorage:', error.message);
      const updated = taskStatuses.map(s => s.id === id ? { ...s, ...updates } : s).sort((a, b) => (a.order || 0) - (b.order || 0));
      setTaskStatuses(updated);
      writeLocalStatuses(updated);
      return { success: true, mode: 'local' };
    }
  };

  const deleteTaskStatus = async (id) => {
    const target = taskStatuses.find(s => s.id === id);
    if (!target) return { success: false, error: 'Status nicht gefunden' };
    if (target.is_default) {
      return { success: false, error: 'Standard-Status kann nicht gelöscht werden' };
    }
    const tasksWithStatus = tasks.filter(t => t.status === target.status_id);
    if (tasksWithStatus.length > 0) {
      return { success: false, error: `${tasksWithStatus.length} Aufgabe${tasksWithStatus.length > 1 ? 'n' : ''} verwenden diesen Status noch. Bitte zuerst die Aufgaben verschieben.` };
    }
    // local-only?
    if (id.startsWith('local_')) {
      const updated = taskStatuses.filter(s => s.id !== id);
      setTaskStatuses(updated);
      writeLocalStatuses(updated);
      return { success: true, mode: 'local' };
    }
    try {
      await databases.deleteDocument(APPWRITE_DB_ID, COLLECTIONS.TASK_STATUSES, id);
      setTaskStatuses(prev => prev.filter(s => s.id !== id));
      return { success: true };
    } catch (error) {
      console.warn('Appwrite deleteTaskStatus fehlgeschlagen, nutze localStorage:', error.message);
      const updated = taskStatuses.filter(s => s.id !== id);
      setTaskStatuses(updated);
      writeLocalStatuses(updated);
      return { success: true, mode: 'local' };
    }
  };

  // ── STATISTICS ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    let total = 0, balanceTotal = 0;
    const byUser = { kasse: 0 };
    
    if (humanUsers && humanUsers.length >= 2) {
      byUser[humanUsers[0].id] = 0;
      byUser[humanUsers[1].id] = 0;
    }

    expenses.forEach(e => {
      const amount = parseFloat(e.amount) || 0;
      const value  = (e.type === 'credit') ? -amount : amount;
      
      total += value; 
      
      const paidBy = e.paid_by || e.paidBy;
      if (paidBy === 'kasse') { 
        byUser['kasse'] = (byUser['kasse'] || 0) + value; 
      } else if (humanUsers && humanUsers.length >= 2) {
        let assignedId = null;
        if (paidBy === humanUsers[0].id || paidBy === humanUsers[0].name || paidBy === '1') assignedId = humanUsers[0].id;
        else if (paidBy === humanUsers[1].id || paidBy === humanUsers[1].name || paidBy === '2') assignedId = humanUsers[1].id;
        
        if (assignedId) { 
          byUser[assignedId] += value; 
          balanceTotal += value; 
        }
      }
    });

    let debtor = null, creditor = null, balance = 0;
    if (humanUsers && humanUsers.length >= 2) {
      const halfTotal = balanceTotal / 2;
      const user1Paid = byUser[humanUsers[0].id] || 0;
      const balanceValue = user1Paid - halfTotal;
      balance = Math.abs(balanceValue);
      debtor = balanceValue > 0 ? humanUsers[1] : humanUsers[0];
      creditor = balanceValue > 0 ? humanUsers[0] : humanUsers[1];
    }

    return { total, balance, byUser, debtor, creditor };
  }, [expenses, humanUsers]);

  const totalCredits = useMemo(() =>
    credits.reduce((sum, c) => sum + (parseFloat(c.revenue_amount || c.amount) || 0), 0),
  [credits]);

  const customersWithCredits = useMemo(() => {
    // Single Source of Truth: customerTotals() rechnet die Provision je Buchung
    // mit dem GESPEICHERTEN Satz (commission_rate_used) — nicht mit dem aktuellen
    // Kundensatz. Sonst würden historische Provisionen bei einer Satzänderung
    // rückwirkend neu berechnet.
    return /** @type {Array<Record<string, any>>} */ (customers.map(customer => {
      const totals = customerTotals(customer, credits);
      return {
        ...totals,
        credits: credits.filter(c => c.customer_id === customer.$id),
        totalCredits: totals.totalRevenue,
      };
    }));
  }, [customers, credits]);
  const customerSegments = useMemo(() => {
    if (!Array.isArray(customersWithCredits)) return [];
    return customersWithCredits.map(customer => {
      const totalRevenue  = customer.totalRevenue || 0;
      const creditCount   = Array.isArray(customer.credits) ? customer.credits.length : 0;
      let tier = 'bronze';
      if      (totalRevenue >= 5000) tier = 'platinum';
      else if (totalRevenue >= 2000) tier = 'gold';
      else if (totalRevenue >= 500)  tier = 'silver';
      const lastTransaction = creditCount > 0
        ? new Date(Math.max(...customer.credits.map(c => new Date(c.transfer_date || 0).getTime()))) : null;
      let daysSinceLastCredit = 0, activity = 'inactive';
      if (lastTransaction && !isNaN(lastTransaction.getTime())) {
        daysSinceLastCredit = Math.floor((Date.now() - lastTransaction.getTime()) / (1000 * 60 * 60 * 24));
        if      (daysSinceLastCredit <= 30) activity = 'active';
        else if (daysSinceLastCredit <= 90) activity = 'moderate';
      }
      const avgAmount     = creditCount > 0 ? totalRevenue / creditCount : 0;
      const customerTasks = tasks.filter(t => t.customer_id === customer.id && t.status !== 'done');
      const frequency     = creditCount >= 5 ? 'high' : creditCount >= 2 ? 'medium' : 'low';
      return {
        id: customer.id, name: customer.name, tier, activity, frequency,
        totalAmount: totalRevenue, creditCount, avgAmount, lastCredit: lastTransaction,
        daysSinceLastCredit, openTasks: customerTasks.length,
        overdueTasks: customerTasks.filter(t => t.due_date && new Date(t.due_date) < new Date()).length,
        customer: { id: customer.id, name: customer.name, email: customer.email, company: customer.company }
      };
    });
  }, [customersWithCredits, tasks]);

  const taskStats = useMemo(() => {
    const byStatus = {};
    taskStatuses.forEach(s => {
      byStatus[s.status_id] = tasks.filter(t => t.status === s.status_id).length;
    });
    // Auch für Tasks mit unbekanntem Status (z.B. gelöschter Status)
    tasks.forEach(t => {
      if (!(t.status in byStatus)) byStatus[t.status] = (byStatus[t.status] || 0) + 1;
    });
    return {
      total: tasks.length,
      byStatus,
      overdue: tasks.filter(t => t.status !== 'done' && t.due_date && new Date(t.due_date) < new Date()).length
    };
  }, [tasks, taskStatuses]);

  const creditStats = useMemo(() => ({
    total: totalCredits,
    count: credits.length,
    byCustomer: customers.map(c => ({
      customerId:   c.id,
      customerName: c.name,
      total: credits.filter(cr => cr.customer_id === c.id).reduce((sum, cr) => sum + (parseFloat(cr.revenue_amount || cr.amount) || 0), 0)
    }))
  }), [credits, customers, totalCredits]);

  const value = {
    user, users, humanUsers, currentUser, isAuthenticated,
    connectionStatus, connectionError,
    login, logout, updateUserName, updateUserPassword, updateUser, addUser, deleteUser,
    settings, updateSettings,
    expenses, addExpense, updateExpense, deleteExpense,
    customers: customersWithCredits, customerSegments,
    addCustomer, updateCustomer, deleteCustomer,
    credits, addCredit, updateCredit, deleteCredit,
    stats, totalCredits, creditStats,
    tasks, taskStatuses, taskStats, taskStatusesLoaded,
  addTask, updateTask, deleteTask, moveTask,
  addTaskStatus, updateTaskStatus, deleteTaskStatus,
    auditLogs, commissionHistory, loading, loadError, reloadData,
    FRANCHISE_RATE,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
};